import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "src/components/layout/dashboard/Layout";
import Breadcrumb from "src/components/ui/Breadcrumb";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";
import InvoiceDocument from "src/components/invoice/InvoiceDocument";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import { toast } from "react-toastify";
import salesApis from "src/api/salesOrder";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { formatAddress } from "src/utils/formatAddress";
import { generatePDF } from "src/utils/pdfGenerator";
import { printElement } from "src/utils/printElement";
import { Loader2, Download, Printer, Pencil } from "lucide-react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as salesActions from "src/redux/slices/salesSlice";
import { Badge } from "src/components/ui/Badge";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import OrderPaymentPanel from "src/components/payments/OrderPaymentPanel";
import paymentApis from "src/api/payments";
import { RefundResult } from "src/types/salePayment.types";
import { userCan } from "src/utils/permissions";

const statusColor = (status: string) =>
  status === "completed" ? "green" : status === "cancelled" ? "red" : "yellow";

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

// Body of the cancel confirmation — what happens to stock and, if money
// was collected, how it goes back: online payments are refunded through
// Razorpay automatically; offline money only counts as returned once staff
// confirm it.
const CancelOrderMessage: React.FC<{
  amountPaid: number;
  refundInfo: { online: number; offline: number } | null;
  warehouseName?: string;
  offlineReturned: boolean;
  onOfflineReturnedChange: (value: boolean) => void;
}> = ({ amountPaid, refundInfo, warehouseName, offlineReturned, onOfflineReturnedChange }) => {
  const effects = (
    <ul className="list-disc space-y-1 pl-5 text-sm">
      <li>
        Reserved stock{warehouseName ? ` at ${warehouseName}` : ""} goes back to available.
      </li>
      <li>Any open payment link is cancelled, so the customer can't pay again.</li>
      <li>This can't be undone.</li>
    </ul>
  );

  if (amountPaid <= 0) {
    return (
      <div className="space-y-3">
        <p>No payment has been collected on this order.</p>
        {effects}
      </div>
    );
  }

  const online = refundInfo?.online ?? 0;
  const offline = refundInfo?.offline ?? (refundInfo ? 0 : amountPaid);
  return (
    <div className="space-y-3">
      <p className="font-medium text-gray-800 dark:text-gray-200">
        {currencyFormatter(amountPaid)} has been collected on this order and will be
        refunded to the customer.
      </p>
      <ul className="space-y-3 text-sm">
        {online > 0 && (
          <li className="rounded-lg bg-blue-50 px-3 py-2 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100">
            <span className="font-semibold">{currencyFormatter(online)} paid online</span> is
            refunded automatically through Razorpay to the customer's original payment
            method (UPI, card or bank). It usually reaches them in 5–7 working days.
          </li>
        )}
        {offline > 0 && (
          <li className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
            <span className="font-semibold">{currencyFormatter(offline)} collected offline</span>{" "}
            has to be returned by hand.
            <label className="mt-2 flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={offlineReturned}
                onChange={(e) => onOfflineReturnedChange(e.target.checked)}
              />
              I've returned {currencyFormatter(offline)} to the customer
            </label>
            {!offlineReturned && (
              <span className="mt-1 block text-xs">
                Leave unticked to return it later — it stays as refund due on the order.
              </span>
            )}
          </li>
        )}
      </ul>
      {effects}
    </div>
  );
};

// One-line summary of what a cancel did with the money.
const refundToastMessage = (orderCode: string, refund: RefundResult | null) => {
  if (!refund) return { type: "success" as const, text: `${orderCode} cancelled` };
  if (refund.error) {
    return {
      type: "warning" as const,
      text: `${orderCode} cancelled, but the refund couldn't start (${refund.error}) — retry from the order page`,
    };
  }
  const parts: string[] = [];
  if (refund.onlineRefunded > 0) {
    parts.push(`${currencyFormatter(refund.onlineRefunded)} refund initiated via Razorpay`);
  }
  if (refund.offlineRefunded > 0) {
    parts.push(`${currencyFormatter(refund.offlineRefunded)} marked as returned`);
  }
  if (refund.failed.length > 0) {
    return {
      type: "warning" as const,
      text: `${orderCode} cancelled. ${parts.join(", ")}${parts.length ? ". " : ""}Refund failed for ${refund.failed.length} payment(s): ${refund.failed[0].message} — retry from the order page`,
    };
  }
  if (refund.due.offline > 0) {
    parts.push(`${currencyFormatter(refund.due.offline)} still to return by hand`);
  }
  return { type: "success" as const, text: `${orderCode} cancelled. ${parts.join(", ")}` };
};

const SalesOrderDetailsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>(null);
  const [downloading, setDownloading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [openingCancel, setOpeningCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // What has been collected, split the way it must be refunded.
  const [refundInfo, setRefundInfo] = useState<{ online: number; offline: number } | null>(
    null
  );
  const [offlineReturned, setOfflineReturned] = useState(false);
  // Set by SalesForm when an order is created with the Online payment mode.
  const openPayNow = !!(location.state as any)?.openPayNow;
  const invoiceRef = useRef<HTMLDivElement>(null);

  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const tenant = user?.tenantDetails;
  const { singular: warehouseLabel } = useWarehouseLabel();
  const { completeSaleOrder } = useAppSelector((state) => state.sales);

  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" || user?.roleDetails?.name?.toLowerCase() === "admin";
  const canEditSales = isTenantAdmin || (permissions?.sales || []).includes("edit");
  const canCollectPayment = userCan(user, "sales", "edit");
  const isOrderOwner = (() => {
    const createdById =
      typeof order?.createdBy === "string" ? order.createdBy : order?.createdBy?._id;
    if (!createdById || !user?._id) return false;
    return String(createdById) === String(user._id);
  })();
  const canEditThisOrder =
    canEditSales && order?.orderStatus === "pending" && (isTenantAdmin || isOrderOwner);

  useEffect(() => {
    if (orderId) getOrderDetails(orderId);
  }, [orderId]);

  useEffect(() => {
    if (completeSaleOrder.data) {
      toast.success("Sale order marked completed");
      dispatch(salesActions.resetCompleteSalesOrder());
      if (orderId) getOrderDetails(orderId);
    } else if (completeSaleOrder.error) {
      toast.error(`Error: ${completeSaleOrder.error}`);
      dispatch(salesActions.resetCompleteSalesOrder());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeSaleOrder]);

  const handleMarkCompleted = () => {
    if (orderId) dispatch(salesActions.completeSalesOrderRequest(orderId));
  };

  // Goods only leave once the order is fully paid (also enforced server-side).
  const balanceDue = order?.balanceDue ?? 0;
  const isFullyPaid = balanceDue <= 0;

  // Loads what's been collected before asking, so the modal can say exactly
  // what has to be refunded and where.
  const requestCancel = async () => {
    setRefundInfo(null);
    setOfflineReturned(false);
    if ((order?.amountPaid || 0) > 0 && orderId) {
      try {
        setOpeningCancel(true);
        const res: any = await paymentApis.getOrderPayments(orderId);
        const received = (res?.data?.payments || []).filter(
          (p: any) => p.status === "success"
        );
        // Net of anything already refunded.
        const sum = (list: any[]) =>
          list.reduce((t, p) => t + p.amount - (p.refundedAmount || 0), 0);
        setRefundInfo({
          online: sum(received.filter((p: any) => p.source === "online")),
          offline: sum(received.filter((p: any) => p.source === "offline")),
        });
      } catch {
        // Fall back to the order's total — the modal still explains it.
      } finally {
        setOpeningCancel(false);
      }
    }
    setShowCancelModal(true);
  };

  const handleCancelOrder = async () => {
    if (!orderId || !order) return;
    try {
      setCancelling(true);
      const res: any = await salesApis.cancelSalesOrder(orderId, {
        offlineRefunded: offlineReturned,
      });
      const message = refundToastMessage(order.orderId, res?.refund || null);
      toast[message.type](message.text);
      setShowCancelModal(false);
      getOrderDetails(orderId);
    } catch (error: any) {
      toast.error(error.message || "Failed to cancel the order");
    } finally {
      setCancelling(false);
    }
  };

  // `silent` refreshes in place — a full-page loader would unmount the
  // payment panel (and any open Pay now modal) mid-payment.
  const getOrderDetails = async (orderId: string, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await salesApis.getOrderById(orderId);
      setOrder(res?.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await generatePDF(invoiceRef, `invoice-${order.orderId}.pdf`);
    } catch (error) {
      toast.error("Failed to generate invoice PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => printElement();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="animate-spin text-primary-600" size={28} />
          <span className="ml-3 text-gray-700 dark:text-gray-300">Loading order...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          Order not found or deleted.
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      meta={{
        title: `Order ${order.orderId} | Invyntra Dashboard`,
        description: "View full sale order details and generate invoices.",
        keywords: "sales order details, invoice, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Breadcrumb
            data={[
              { label: "Sales", path: "/app/sale/list" },
              { label: `#${order.orderId}` },
            ]}
          />
          <div className="flex items-center gap-2">
            {order.orderStatus === "completed" && (
              <>
                <Button onClick={handleDownload} isLoading={downloading} disabled={downloading}>
                  <Download size={16} className="mr-1.5" />
                  Download PDF
                </Button>
                <Button variant="outline" onClick={handlePrint}>
                  <Printer size={16} className="mr-1.5" />
                  Print
                </Button>
              </>
            )}
            {canEditThisOrder && (
              <Button onClick={() => navigate(`/app/sale/${order._id}/edit`)}>
                <Pencil size={16} className="mr-1.5" />
                Edit order
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/app/sale/list")}>
              Back to sales
            </Button>
          </div>
        </div>

        <div className="space-y-6 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              Order #{order.orderId}
            </h1>
            {order.orderStatus && (
              <Badge color={statusColor(order.orderStatus)} text={capitalize(order.orderStatus)} />
            )}
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Order date: {formatDateTime(order.orderDate || order.createdAt, "DD MMM YYYY")}
            </span>
          </div>

          {/* Customer Info */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3 dark:text-gray-100">
              Customer Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700 dark:text-gray-300">
              <p>
                <span className="font-medium">Name: </span>
                {order.customerId?.name}
              </p>
              <p>
                <span className="font-medium">Mobile: </span>
                {order.customerId?.mobile}
              </p>
              <p>
                <span className="font-medium">Email: </span>
                {order.customerId?.email || "-"}
              </p>
              <p>
                <span className="font-medium">Address: </span>
                {formatAddress(order.customerId) || "-"}
              </p>
              <p>
                <span className="font-medium">GSTIN: </span>
                {order.customerId?.gstNumber || "-"}
              </p>
              <p>
                <span className="font-medium">{warehouseLabel}: </span>
                {order.warehouseId?.name || "-"}
              </p>
            </div>
          </Card>

          {/* Products */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3 dark:text-gray-100">
              Ordered Products
            </h3>

            <TableV2
              columns={[
                {
                  key: "productId.name",
                  header: "Product",
                  accessor: ({ row }) => row.productId?.name,
                },
                {
                  key: "sku",
                  header: "SKU",
                  accessor: ({ row }) => row.sku || row.productId?.sku || "-",
                },
                {
                  key: "hsnCode",
                  header: "HSN",
                  accessor: ({ row }) =>
                    row.hsnCode || row.productId?.hsnCode || "-",
                },
                { key: "quantity", header: "Qty", align: "right" },
                {
                  key: "sellingPrice",
                  header: "Price",
                  align: "right",
                  accessor: ({ row }) =>
                    currencyFormatter(row.sellingPrice.toFixed(2)),
                },
                {
                  key: "discountPercent",
                  header: "Disc (%)",
                  align: "right",
                },
                {
                  key: "taxPercent",
                  header: "GST %",
                  align: "right",
                  accessor: ({ row }) => `${row.taxPercent ?? 0}%`,
                },
                {
                  key: "taxAmount",
                  header: "Tax",
                  align: "right",
                  accessor: ({ row }) =>
                    currencyFormatter((row.taxAmount ?? 0).toFixed(2), "IN", 2),
                },
                {
                  key: "totalPrice",
                  header: "Total",
                  align: "right",
                  accessor: ({ row }) =>
                    currencyFormatter(row.totalPrice.toFixed(2), "IN", 2),
                },
              ]}
              data={order.items || []}
            />
          </Card>

          <OrderPaymentPanel
            order={order}
            canCollect={canCollectPayment}
            autoOpenCollect={openPayNow}
            onPaymentsChanged={() => orderId && getOrderDetails(orderId, true)}
          />

          {/* Meta Info */}
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <p>
              <span className="font-medium text-gray-700 dark:text-gray-300">
                Created By:
              </span>{" "}
              {order.createdBy?.firstName || "Staff"}
            </p>
            <p>
              <span className="font-medium text-gray-700 dark:text-gray-300">
                Created At:
              </span>{" "}
              {formatDateTime(order.createdAt, "DD/MM/YYYY hh:mm A")}
            </p>
          </div>

          {/* Fulfillment actions */}
          {order.orderStatus === "pending" ? (
            <Card>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Fulfill this order
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Stock is currently reserved at{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {order.warehouseId?.name || `the selected ${warehouseLabel.toLowerCase()}`}
                </span>
                . Mark it completed once it's fully paid and handed over, or cancel
                to release the hold.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleMarkCompleted}
                  isLoading={completeSaleOrder.loading}
                  disabled={!isFullyPaid || completeSaleOrder.loading}
                  title={isFullyPaid ? undefined : "Collect the full payment first"}
                >
                  Mark completed
                </Button>
                <Button
                  variant="outline"
                  color="danger"
                  onClick={requestCancel}
                  isLoading={openingCancel}
                  disabled={completeSaleOrder.loading || openingCancel}
                >
                  Cancel order
                </Button>
              </div>
              {!isFullyPaid && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  {currencyFormatter(balanceDue)} is still due — collect the full payment
                  before completing this order.
                </p>
              )}
            </Card>
          ) : order.orderStatus === "completed" ? (
            <Card>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Completed on{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {formatDateTime(order.completedAt, "DD MMM YYYY, hh:mm A")}
                </span>
                . Stock has been deducted from{" "}
                {order.warehouseId?.name || `the ${warehouseLabel.toLowerCase()}`}.
              </p>
            </Card>
          ) : order.orderStatus === "cancelled" ? (
            <Card>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This order was cancelled — its stock reservation has been released.
              </p>
            </Card>
          ) : null}
        </div>
      </div>

      {showCancelModal && (
        <ConfirmationModal
          title={`Cancel order ${order.orderId}?`}
          message={
            <CancelOrderMessage
              amountPaid={order.amountPaid || 0}
              refundInfo={refundInfo}
              warehouseName={order.warehouseId?.name}
              offlineReturned={offlineReturned}
              onOfflineReturnedChange={setOfflineReturned}
            />
          }
          variant="danger"
          confirmLabel={(order.amountPaid || 0) > 0 ? "Cancel & refund" : "Cancel order"}
          cancelLabel="Keep order"
          onConfirm={handleCancelOrder}
          onCancel={() => setShowCancelModal(false)}
          open={showCancelModal}
          isLoading={cancelling}
        />
      )}

      <div className="fixed left-[-9999px] top-0" aria-hidden>
        <InvoiceDocument
          ref={invoiceRef}
          order={order}
          tenant={tenant}
          className="print-target"
        />
      </div>
    </DashboardLayout>
  );
};

export default SalesOrderDetailsPage;
